import React, { useEffect, useState } from "react";
import axios from "axios";
import { Loader2, Sparkles, Send, Trash2, Eye, AlertCircle, RefreshCw, Plus, FileText, Edit3, TrendingUp, X } from "lucide-react";
import { API } from "@/lib/api";

/**
 * QAComposerPanel
 *
 * Admin tool to grow the public Q&A library at /q/*. Two ways to author:
 *
 *   1. Ask Compass for a draft. type a tenant question + optional state +
 *      persona. Backend runs Sonnet against the same prompt the public
 *      Brain uses, returns a structured draft. Cost is recorded under the
 *      admin's spend (no per-user attribution).
 *
 *   2. Hand-write. fill the form fields directly.
 *
 * Either way the admin reviews + edits before pressing Publish.
 *
 * The list at the top shows every published entry with edit + delete +
 * preview-link affordances so the admin can iterate the library over time.
 *
 * Server-side endpoints used:
 *   GET    /api/admin/qa/list        — every entry (published + drafts)
 *   POST   /api/admin/qa/generate    — Sonnet-drafted answer (no save)
 *   POST   /api/admin/qa/publish     — save / overwrite by slug
 *   DELETE /api/admin/qa/{slug}      — remove
 */

const STATES = ["AU", "QLD", "NSW", "VIC", "SA", "WA", "TAS", "ACT", "NT"];

const EMPTY_DRAFT = {
  slug: "",
  state: "AU",
  title: "",
  teaser: "",
  meta_description: "",
  persona: "Australian tenant",
  question: "",
  answer: "",
  citations: [],
};

function slugify(s) {
  return (s || "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 80);
}

export default function QAComposerPanel({ token }) {
  const [list, setList] = useState([]);
  const [listLoading, setListLoading] = useState(true);
  const [listErr, setListErr] = useState(null);

  const [draft, setDraft] = useState(EMPTY_DRAFT);
  const [genQuestion, setGenQuestion] = useState("");
  const [genState, setGenState] = useState("AU");
  const [genPersona, setGenPersona] = useState("");
  const [generating, setGenerating] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [editingSlug, setEditingSlug] = useState(null);
  const [feedback, setFeedback] = useState(null);
  // Gap-finder state — surfaces high-volume Brain questions without a /q page
  const [gapsOpen, setGapsOpen] = useState(false);
  const [gapsLoading, setGapsLoading] = useState(false);
  const [gaps, setGaps] = useState(null);

  const auth = { headers: { Authorization: `Bearer ${token}` } };

  const loadList = async () => {
    setListLoading(true);
    setListErr(null);
    try {
      const { data } = await axios.get(`${API}/admin/qa/list`, auth);
      setList(data.items || []);
    } catch (e) {
      setListErr(e.response?.data?.detail || "Couldn't load Q&A list.");
    } finally {
      setListLoading(false);
    }
  };

  useEffect(() => { loadList(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const showFeedback = (kind, text) => {
    setFeedback({ kind, text });
    setTimeout(() => setFeedback(null), 5000);
  };

  const onAskCompass = async () => {
    if (!genQuestion.trim() || genQuestion.trim().length < 10) {
      showFeedback("error", "Question needs at least 10 characters.");
      return;
    }
    setGenerating(true);
    try {
      const { data } = await axios.post(
        `${API}/admin/qa/generate`,
        { question: genQuestion.trim(), state: genState, persona: genPersona.trim() || null },
        auth,
      );
      // The generate endpoint returns: suggested_slug, title, teaser, meta_description, answer, citations, original_question
      setDraft({
        slug: data.suggested_slug || slugify(data.title || genQuestion),
        state: (data.state || genState).toUpperCase(),
        title: data.title || "",
        teaser: data.teaser || "",
        meta_description: data.meta_description || "",
        persona: genPersona.trim() || "Australian tenant",
        question: data.original_question || genQuestion.trim(),
        answer: data.answer || "",
        citations: data.citations || [],
      });
      setEditingSlug(null);
      showFeedback("success", "Compass drafted it. review and edit below before publishing.");
    } catch (e) {
      showFeedback("error", e.response?.data?.detail || "Generation failed.");
    } finally {
      setGenerating(false);
    }
  };

  const onPublish = async () => {
    if (!draft.slug || !draft.title || !draft.question || !draft.answer) {
      showFeedback("error", "Slug, title, question and answer are all required.");
      return;
    }
    setPublishing(true);
    try {
      const payload = {
        slug: draft.slug,
        state: draft.state.toUpperCase(),
        title: draft.title.trim(),
        teaser: (draft.teaser || "").trim(),
        meta_description: (draft.meta_description || "").trim(),
        persona: (draft.persona || "Australian tenant").trim(),
        question: draft.question.trim(),
        answer: draft.answer.trim(),
        citations: (draft.citations || []).filter(Boolean),
      };
      await axios.post(`${API}/admin/qa/publish`, payload, auth);
      showFeedback("success", editingSlug ? "Updated." : `Published. visit /q/${draft.slug}`);
      setDraft(EMPTY_DRAFT);
      setEditingSlug(null);
      setGenQuestion("");
      await loadList();
    } catch (e) {
      showFeedback("error", e.response?.data?.detail || "Publish failed.");
    } finally {
      setPublishing(false);
    }
  };

  const onEdit = (entry) => {
    setDraft({
      slug: entry.slug,
      state: entry.state || "AU",
      title: entry.title || "",
      teaser: entry.teaser || "",
      meta_description: entry.meta_description || "",
      persona: entry.persona || "Australian tenant",
      question: entry.question || "",
      answer: entry.answer || "",
      citations: entry.citations || [],
    });
    setEditingSlug(entry.slug);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const onDelete = async (slug) => {
    if (!window.confirm(`Delete /q/${slug}? this cannot be undone.`)) return;
    try {
      await axios.delete(`${API}/admin/qa/${slug}`, auth);
      showFeedback("success", "Deleted.");
      await loadList();
    } catch (e) {
      showFeedback("error", e.response?.data?.detail || "Delete failed.");
    }
  };

  const startBlank = () => {
    setDraft(EMPTY_DRAFT);
    setEditingSlug(null);
    setGenQuestion("");
  };

  const loadGaps = async () => {
    setGapsLoading(true);
    setGapsOpen(true);
    try {
      const { data } = await axios.get(`${API}/admin/qa/gaps?days=90&limit=20`, auth);
      setGaps(data);
    } catch (e) {
      showFeedback("error", e.response?.data?.detail || "Couldn't load gap analysis.");
      setGapsOpen(false);
    } finally {
      setGapsLoading(false);
    }
  };

  const pickGap = (suggestion) => {
    setGenQuestion(suggestion.example_question);
    setGenState(suggestion.state || "AU");
    setGapsOpen(false);
    setTimeout(() => {
      const el = document.querySelector('[data-testid="qa-gen-question"]');
      if (el) el.scrollIntoView({ behavior: "smooth", block: "center" });
    }, 100);
  };

  return (
    <div className="space-y-6" data-testid="qa-composer-panel">

      {feedback && (
        <div
          className={`card border-l-4 ${feedback.kind === "success" ? "border-l-[#1B6C62] bg-[#EFF8F6]" : "border-l-[#B35D4C] bg-[#FFF4EC]"}`}
          data-testid="qa-feedback"
        >
          <div className="flex items-start gap-2 text-sm text-[#0D2B52]">
            {feedback.kind === "success"
              ? <Sparkles size={14} className="text-[#1B6C62] mt-0.5" strokeWidth={1.75} />
              : <AlertCircle size={14} className="text-[#B35D4C] mt-0.5" strokeWidth={1.75} />}
            <span>{feedback.text}</span>
          </div>
        </div>
      )}

      {/* Compass-draft panel */}
      <div className="card border-l-4 border-l-[#1B6C62]">
        <div className="flex items-center gap-2 mb-3">
          <Sparkles size={14} strokeWidth={1.75} className="text-[#1B6C62]" />
          <h3 className="font-serif text-lg text-[#0D2B52]">Ask Compass to draft one</h3>
        </div>
        <p className="text-xs text-[#6B7280] mb-4">
          Type the raw tenant question. Compass writes a publishable answer + title + meta description. Review and edit before publishing.
        </p>
        <textarea
          rows={3}
          value={genQuestion}
          onChange={(e) => setGenQuestion(e.target.value)}
          placeholder="e.g. My landlord wants to raise my rent by $120 a week. Can they?"
          className="input-field w-full text-sm mb-3"
          data-testid="qa-gen-question"
        />
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-4">
          <div>
            <label className="label-mono mb-1 block">State</label>
            <select
              value={genState}
              onChange={(e) => setGenState(e.target.value)}
              className="input-field w-full text-sm"
              data-testid="qa-gen-state"
            >
              {STATES.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
          </div>
          <div className="sm:col-span-2">
            <label className="label-mono mb-1 block">Persona (optional)</label>
            <input
              type="text"
              value={genPersona}
              onChange={(e) => setGenPersona(e.target.value)}
              placeholder="e.g. First-time renter signing a 12-month lease"
              className="input-field w-full text-sm"
              data-testid="qa-gen-persona"
            />
          </div>
        </div>
        <div className="flex gap-2 flex-wrap">
          <button
            onClick={onAskCompass}
            disabled={generating}
            className="btn-teal text-sm inline-flex items-center gap-2 disabled:opacity-50"
            data-testid="qa-gen-submit"
          >
            {generating ? <Loader2 size={14} className="animate-spin" /> : <Sparkles size={14} strokeWidth={1.75} />}
            {generating ? "Compass is writing…" : "Ask Compass"}
          </button>
          <button
            onClick={loadGaps}
            className="btn-secondary text-sm inline-flex items-center gap-2"
            data-testid="qa-gaps-open"
          >
            <TrendingUp size={14} strokeWidth={1.75} /> Suggest the next question
          </button>
          <button
            onClick={startBlank}
            className="btn-ghost text-sm inline-flex items-center gap-2"
            data-testid="qa-blank-form"
          >
            <Edit3 size={14} strokeWidth={1.75} /> Or write by hand
          </button>
        </div>
      </div>

      {/* Draft editor */}
      <div className="card" data-testid="qa-draft-card">
        <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
          <div className="flex items-center gap-2">
            <FileText size={14} strokeWidth={1.75} className="text-[#0D2B52]" />
            <h3 className="font-serif text-lg text-[#0D2B52]">
              {editingSlug ? `Editing /q/${editingSlug}` : "New Q&A draft"}
            </h3>
          </div>
          {editingSlug && (
            <button onClick={startBlank} className="btn-ghost text-xs inline-flex items-center gap-1" data-testid="qa-cancel-edit">
              <Plus size={11} strokeWidth={1.75} /> New draft
            </button>
          )}
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-3">
          <div>
            <label className="label-mono mb-1 block">Slug (URL path)</label>
            <input
              type="text"
              value={draft.slug}
              onChange={(e) => setDraft({ ...draft, slug: slugify(e.target.value) })}
              onBlur={(e) => !e.target.value && setDraft({ ...draft, slug: slugify(draft.title) })}
              placeholder="e.g. is-it-legal-to-record-my-landlord-australia"
              className="input-field w-full text-sm font-mono"
              data-testid="qa-draft-slug"
              disabled={Boolean(editingSlug)}
            />
            <div className="text-[10px] text-[#6B7280] mt-1">/q/{draft.slug || "your-slug"}</div>
          </div>
          <div>
            <label className="label-mono mb-1 block">State</label>
            <select
              value={draft.state}
              onChange={(e) => setDraft({ ...draft, state: e.target.value })}
              className="input-field w-full text-sm"
              data-testid="qa-draft-state"
            >
              {STATES.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
          </div>
        </div>

        <div className="mb-3">
          <label className="label-mono mb-1 block">Page title</label>
          <input
            type="text"
            value={draft.title}
            onChange={(e) => setDraft({ ...draft, title: e.target.value })}
            placeholder="The headline that shows on /q/* and in Google"
            className="input-field w-full text-sm"
            data-testid="qa-draft-title"
            maxLength={140}
          />
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-3">
          <div>
            <label className="label-mono mb-1 block">Teaser (1 line)</label>
            <textarea
              rows={2}
              value={draft.teaser}
              onChange={(e) => setDraft({ ...draft, teaser: e.target.value })}
              className="input-field w-full text-sm"
              data-testid="qa-draft-teaser"
              maxLength={220}
            />
          </div>
          <div>
            <label className="label-mono mb-1 block">Meta description (SEO)</label>
            <textarea
              rows={2}
              value={draft.meta_description}
              onChange={(e) => setDraft({ ...draft, meta_description: e.target.value })}
              className="input-field w-full text-sm"
              data-testid="qa-draft-meta"
              maxLength={170}
            />
          </div>
        </div>

        <div className="mb-3">
          <label className="label-mono mb-1 block">Persona</label>
          <input
            type="text"
            value={draft.persona}
            onChange={(e) => setDraft({ ...draft, persona: e.target.value })}
            className="input-field w-full text-sm"
            data-testid="qa-draft-persona"
          />
        </div>

        <div className="mb-3">
          <label className="label-mono mb-1 block">Question</label>
          <textarea
            rows={2}
            value={draft.question}
            onChange={(e) => setDraft({ ...draft, question: e.target.value })}
            className="input-field w-full text-sm"
            data-testid="qa-draft-question"
          />
        </div>

        <div className="mb-3">
          <label className="label-mono mb-1 block">Answer (markdown OK, line breaks preserved)</label>
          <textarea
            rows={12}
            value={draft.answer}
            onChange={(e) => setDraft({ ...draft, answer: e.target.value })}
            className="input-field w-full text-sm font-serif leading-relaxed"
            data-testid="qa-draft-answer"
          />
          <div className="text-[10px] text-[#6B7280] mt-1">
            {(draft.answer || "").length} chars · ~{Math.ceil((draft.answer || "").split(/\s+/).filter(Boolean).length / 200)} min read
          </div>
        </div>

        <div className="mb-4">
          <label className="label-mono mb-1 block">
            Citations <span className="text-[#6B7280] normal-case">(one per line, e.g. &ldquo;Residential Tenancies Act 2010 (NSW), s 55&rdquo;)</span>
          </label>
          <textarea
            rows={3}
            value={(draft.citations || []).join("\n")}
            onChange={(e) => setDraft({
              ...draft,
              citations: e.target.value.split("\n").map((s) => s.trim()).filter(Boolean),
            })}
            className="input-field w-full text-xs font-mono"
            data-testid="qa-draft-citations"
          />
        </div>

        <div className="flex items-center gap-2 flex-wrap">
          <button
            onClick={onPublish}
            disabled={publishing}
            className="btn-teal text-sm inline-flex items-center gap-2 disabled:opacity-50"
            data-testid="qa-publish-btn"
          >
            {publishing ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} strokeWidth={1.75} />}
            {editingSlug ? "Save changes" : "Publish to /q"}
          </button>
          {draft.slug && (
            <a
              href={`/q/${draft.slug}`}
              target="_blank"
              rel="noreferrer"
              className="btn-ghost text-sm inline-flex items-center gap-2 border border-[#E5E4DE]"
              data-testid="qa-preview-link"
            >
              <Eye size={14} strokeWidth={1.75} /> Preview live page
            </a>
          )}
        </div>
      </div>

      {/* Published list */}
      <div className="card" data-testid="qa-list-card">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-serif text-lg text-[#0D2B52]">Published Q&amp;A library ({list.length})</h3>
          <button
            onClick={loadList}
            className="btn-ghost text-xs inline-flex items-center gap-1"
            disabled={listLoading}
            data-testid="qa-list-refresh"
          >
            <RefreshCw size={11} strokeWidth={1.75} className={listLoading ? "animate-spin" : ""} /> Refresh
          </button>
        </div>

        {listLoading ? (
          <div className="text-center py-8" data-testid="qa-list-loading">
            <Loader2 size={20} className="animate-spin text-[#1B6C62] mx-auto" />
          </div>
        ) : listErr ? (
          <div className="text-sm text-[#B35D4C]" data-testid="qa-list-error">{listErr}</div>
        ) : list.length === 0 ? (
          <div className="text-sm text-[#6B7280] italic" data-testid="qa-list-empty">
            No Q&amp;A entries yet. Publish your first one above.
          </div>
        ) : (
          <div className="divide-y divide-[#EFEDE5]" data-testid="qa-list">
            {list.map((entry) => (
              <div
                key={entry.slug}
                className="py-3 flex items-start justify-between gap-3 flex-wrap"
                data-testid={`qa-row-${entry.slug}`}
              >
                <div className="flex-1 min-w-0">
                  <div className="flex items-baseline gap-2 flex-wrap">
                    <span className="font-mono text-[10px] uppercase tracking-wider text-[#1B6C62]">
                      {entry.state || "AU"}
                    </span>
                    <span className="font-serif text-sm text-[#0D2B52] truncate">{entry.title}</span>
                  </div>
                  <div className="text-[11px] text-[#6B7280] mt-0.5 font-mono truncate">/q/{entry.slug}</div>
                </div>
                <div className="flex items-center gap-1.5">
                  <a
                    href={`/q/${entry.slug}`}
                    target="_blank"
                    rel="noreferrer"
                    className="btn-ghost text-xs inline-flex items-center gap-1"
                    data-testid={`qa-view-${entry.slug}`}
                  >
                    <Eye size={11} strokeWidth={1.75} /> View
                  </a>
                  <button
                    onClick={() => onEdit(entry)}
                    className="btn-ghost text-xs inline-flex items-center gap-1"
                    data-testid={`qa-edit-${entry.slug}`}
                  >
                    <Edit3 size={11} strokeWidth={1.75} /> Edit
                  </button>
                  <button
                    onClick={() => onDelete(entry.slug)}
                    className="btn-ghost text-xs inline-flex items-center gap-1 text-[#B35D4C] hover:text-[#9c4d3e]"
                    data-testid={`qa-delete-${entry.slug}`}
                  >
                    <Trash2 size={11} strokeWidth={1.75} /> Delete
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
      {/* Gap-finder modal */}
      {gapsOpen && (
        <div
          className="fixed inset-0 z-[60] bg-black/50 flex items-center justify-center p-4"
          onClick={() => setGapsOpen(false)}
          data-testid="qa-gaps-modal"
        >
          <div
            className="bg-[#FAF9F6] border border-[#E5E4DE] rounded-sm max-w-2xl w-full max-h-[85vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between p-5 border-b border-[#E5E4DE] sticky top-0 bg-[#FAF9F6]">
              <div className="flex items-center gap-2">
                <TrendingUp size={16} strokeWidth={1.5} className="text-[#1B6C62]" />
                <h3 className="font-serif text-xl text-[#0D2B52]">Gaps in the Q&amp;A library</h3>
              </div>
              <button onClick={() => setGapsOpen(false)} className="btn-ghost p-1" aria-label="Close" data-testid="qa-gaps-close">
                <X size={16} strokeWidth={1.5} />
              </button>
            </div>
            <div className="p-5">
              {gapsLoading ? (
                <div className="text-center py-12" data-testid="qa-gaps-loading">
                  <Loader2 size={20} className="animate-spin text-[#1B6C62] mx-auto" />
                  <p className="text-xs text-[#6B7280] mt-3">Reading Brain search history…</p>
                </div>
              ) : !gaps || (gaps.suggestions || []).length === 0 ? (
                <div className="text-sm text-[#6B7280]" data-testid="qa-gaps-empty">
                  Not enough Brain question data to surface clear gaps yet. Try again in a week or two once more renters search the Brain.
                </div>
              ) : (
                <>
                  <p className="text-xs text-[#6B7280] mb-4 leading-relaxed">
                    Top topics renters asked the Brain about in the last <span className="font-mono">{gaps.window_days}</span> days but you haven&apos;t written a /q page for. Click one to load it into the Compass drafter.
                  </p>
                  <div className="divide-y divide-[#EFEDE5]" data-testid="qa-gaps-list">
                    {gaps.suggestions.map((s, i) => (
                      <button
                        key={i}
                        onClick={() => pickGap(s)}
                        className="w-full text-left py-3 px-2 hover:bg-[#EFEDE5]/50 rounded-sm transition-colors"
                        data-testid={`qa-gap-${i}`}
                      >
                        <div className="flex items-baseline justify-between gap-2 mb-1">
                          <div className="flex items-center gap-2">
                            <span className="font-mono text-[10px] uppercase tracking-wider text-[#1B6C62]">{s.state}</span>
                            <span className="font-mono text-[10px] text-[#B35D4C]">{s.count}× asked</span>
                          </div>
                          <span className="text-[10px] text-[#6B7280] font-mono truncate max-w-[40%]">
                            keywords: {(s.keywords || []).join(", ")}
                          </span>
                        </div>
                        <p className="text-sm text-[#0D2B52] font-serif leading-relaxed">{s.example_question}</p>
                        {s.all_questions && s.all_questions.length > 1 && (
                          <p className="text-[11px] text-[#6B7280] italic mt-1">
                            +{s.all_questions.length - 1} similar variation{s.all_questions.length > 2 ? "s" : ""}
                          </p>
                        )}
                      </button>
                    ))}
                  </div>
                  <div className="mt-4 text-[10px] font-mono text-[#6B7280] text-center">
                    {gaps.total_questions_sampled} Brain questions sampled · {gaps.clusters_found} topic clusters detected
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
